import {getLogger} from '../../logger.js';
import {WebsocketConnectionFactory, WebsocketConnection} from './types.js';
import * as GatewayEncoders from '../encoders.js';
import {IOGateway} from '../../../gateway';

const logger = getLogger('gateway.ws.common');

const ConnectionStates = Object.freeze({
    _initial:  -1,
    connecting: 0,
    connected:  1,
    closing:    2,
    closed:     3,
});
type ConnectionState = keyof typeof ConnectionStates;

abstract class InternalConnection<T> {
    constructor(codec: GatewayEncoders.Codec<T, string>,
                maxEnqueued: number,
                retry?: Retry) {
        this.codec = codec;
        this.maxEnqueued = maxEnqueued;
        this.retry = retry;
    }

    state: ConnectionState = '_initial';
    codec: GatewayEncoders.Codec<T, string>;
    socket?: WebSocket;
    maxEnqueued: number;
    queue: string[] = [];
    retry?: Retry;
    reconnectAttempt = 0;
    reconnectTimeout?: ReturnType<typeof setTimeout>;
    abstract createSocket(): WebSocket;
}

const DEFAULT_RETRY = {
    min: 1000 + (Math.random() * 4000), // between 1 and 5 seconds
    max: 60 * 1000, // 60 seconds
    factor: 1.3
};

function calculateReconnectDelay(attempt: number, retry?: Partial<typeof DEFAULT_RETRY>) {
    const min = Math.max(1000, retry?.min ?? DEFAULT_RETRY.min);
    const max = Math.max(min, retry?.max ?? DEFAULT_RETRY.max);
    const factor = Math.max(1.0, retry?.factor ?? DEFAULT_RETRY.factor);
    const delay = Math.round(Math.min(max, min * Math.pow(factor, attempt)));
    return delay;
}

class WsConnection<T> implements WebsocketConnection<T> {
    readonly #internal: InternalConnection<T>;
    constructor(uri: string | URL, internal: InternalConnection<T>) {
        this.uri = typeof uri === 'string' ? uri : uri.href;
        this.#internal = internal;
    }

    readonly uri: string;

    onConnect?: (protocol?: string) => void
    onReceive?: (msg: T) => void
    onError?: (e: Error) => void
    onDisconnect?: () => void
    onClose?: () => void

    private createAndInit() {
        if (logger.enabledFor('debug')) {
            logger.debug(`connecting to ${this.uri}`);
        }
        try {
            const c = this.#internal.createSocket()
            this.initSocket(c);
        } catch(e) {
            if (this.onError) {
                this.onError(e instanceof Error ? e : new Error(`Failed to create socket: ${e}`));
            } else {
                logger.warn(`init socket error: ${e}`, e);
            }
        }
    }

    private initSocket(c: WebSocket) {
        c.onopen = () => {
            logger.info(`connection to ${this.uri} is up.`);
            const wasConnecting = this.#internal.state === 'connecting';
            if (this.#internal.queue.length > 0) {
                logger.debug(`flushing ${this.#internal.queue.length} pending messages`);
                while (this.#internal.queue.length > 0) {
                    const data = this.#internal.queue.shift();
                    if (data) {
                        if (wasConnecting) {
                            c.send(data);
                        }
                        else {
                            logger.warn(`connection to ${this.uri} is ${this.#internal.state} state, dropping message ${data}`);
                        }
                    }
                }
            }
            if (this.#internal.state === 'closing') {
                c.close();
                return;
            }
            this.#internal.state = 'connected';
            this.#internal.socket = c;
            if (this.onConnect) {
                this.onConnect(c.protocol);
            }
        }
        c.onerror = (e: Event) => {
            if (this.onError) {
                this.onError(e['error'] as Error);
            } else {
                logger.warn(`got error: ${e}`, e);
            }
        }
        c.onmessage = (msg) => {
            const data = this.#internal.codec.decode(msg.data);
            if (logger.enabledFor('debug')) {
                logger.debug(`got message ${JSON.stringify(data)} from ${this.uri}`);
            }
            if (this.onReceive) {
                this.onReceive(data);
            }
        };
        c.onclose = (e: CloseEvent) => {
            let wasOpen = false;
            if (this.#internal.socket !== undefined) {
                wasOpen = true;
                this.#internal.socket = undefined;
            }
            if (this.#internal.state === 'closing') {
                if (wasOpen) {
                    if (this.onDisconnect) {
                        this.onDisconnect();
                    }
                }
                this.#internal.state = 'closed';
                logger.info(`connection to ${this.uri} has closed (${e.reason}:${e.code})`);
                if (this.onClose) {
                    this.onClose();
                }
            }
            else {
                const wasConnected = this.#internal.state === 'connected';
                if (this.#internal.retry === false) {
                    this.#internal.state = '_initial';
                }
                else {
                    if (this.#internal.state !== 'connecting') {
                        this.#internal.reconnectAttempt = 0;
                        this.#internal.state = 'connecting';
                    }
                    const reconnectDelay = calculateReconnectDelay(this.#internal.reconnectAttempt, this.#internal.retry);
                    this.#internal.reconnectAttempt ++;
                    if (logger.enabledFor(`debug`)) {
                        logger.debug(`will reconnect to ${this.uri} in ${reconnectDelay} ms. [attempt: ${this.#internal.reconnectAttempt}]`);
                    }
                    this.#internal.reconnectTimeout = setTimeout(() => {
                        this.createAndInit();
                    }, reconnectDelay);
                }
                if (wasConnected) {
                    logger.info(`connection to ${this.uri} disconnected`);
                    if (this.onDisconnect) {
                        this.onDisconnect();
                    }
                }
            }
        }
    }

    connect() {
        if (this.#internal.state !== '_initial') {
            return;
        }
        this.#internal.state = 'connecting';

        this.createAndInit();
    }

    close() {
        if (this.#internal.state === 'closed' || this.#internal.state === 'closing') {
            return;
        }
        const wasConnecting = this.#internal.state === 'connecting';
        this.#internal.state = 'closing';
        const socket = this.#internal.socket;
        if (logger.enabledFor('debug')) {
            logger.debug(`closing connection to ${this.uri} (opened?: ${socket !== undefined})`);
        }
        if (this.#internal.reconnectTimeout) {
            clearTimeout(this.#internal.reconnectTimeout);
            this.#internal.reconnectTimeout = undefined;
        }

        if (socket) {
            this.#internal.socket = undefined;
            socket.close();
        }
        else if (!wasConnecting) {
            this.#internal.state = 'closed';
            if (this.onClose) {
                this.onClose();
            }
        } else {
            // if wasConnecting wait for close/open
        }
    }

    send(msg: T) {
        if (this.#internal.state === 'closing' || this.#internal.state === 'closed') {
            throw new Error(`connection to ${this.uri} is closed.`);
        }
        const socket = this.#internal.socket;
        const enqueue = socket === undefined && this.#internal.maxEnqueued > 0 && this.#internal.queue.length < this.#internal.maxEnqueued;
        if (logger.enabledFor('debug')) {
            if (logger.enabledFor('debug')) {
                let action: 'sending' | 'enqueueing' | 'dropping';
                if (socket !== undefined) {
                    action  = 'sending';
                } else if (enqueue) {
                    action = 'enqueueing';
                }
                else {
                    action = 'dropping';
                }
                logger.debug(`${action} message ${JSON.stringify(msg)} to ${this.uri}`);
            }
        }
        if (socket || enqueue) {
            const data = this.#internal.codec.encode(msg);
            if (socket) {
                socket.send(data);
            } else {
                this.#internal.queue.push(data);
            }
        }
    }

    toJSON() {
        return {uri: this.uri, state: this.#internal.state, enqueued: this.#internal.queue.length, attempts: this.#internal.reconnectAttempt};
    }
}

function wsConstructor(websocket?: typeof WebSocket): (typeof WebSocket) {
    if (websocket) {
        return websocket;
    }
    if (globalThis.WebSocket) {
        return globalThis.WebSocket;
    }
    else {
        return WebSocket;
    }
}

class WsConnectionFactory<T> implements WebsocketConnectionFactory<T> {
    readonly #codec: GatewayEncoders.Codec<T, string>;
    readonly #retry?: Retry;
    readonly #websocket?: typeof WebSocket;
    readonly #connections = new Array<WsConnection<T>>();
    constructor(codec: GatewayEncoders.Codec<T, string>, retry?: Retry, websocket?: typeof WebSocket) {
        this.#codec = codec;
        this.#retry = retry;
        this.#websocket = websocket;
    }

    create(uri: string | URL, protocols?: string|string[], maxEnqueued: number = -1): WebsocketConnection<T> {
        const websocketOverride = this.#websocket;
        const internal = new class extends InternalConnection<T> {
            createSocket() {
                const WebSocketConstructor = wsConstructor(websocketOverride);
                return new WebSocketConstructor(uri, protocols);
            }
        }(this.#codec, maxEnqueued, this.#retry);
        const wsc = new WsConnection(uri, internal);
        this.#connections.push(wsc);
        return wsc;
    }

    close(): void {
        while(this.#connections.length > 0) {
            const connection = this.#connections.pop();
            if (connection !== undefined) {
                connection.close();
            }
        }

    }

}

export type Retry = Readonly<{
    min?: number,
    max?: number,
    factor?: number
} | false>


export type WebSocketConnectionFactoryOptions<T extends IOGateway.Message<unknown>> = {
    /**
     * Codec used to encode/decode messages. Defaults to JSON.
     */
    codec?: GatewayEncoders.Codec<T, string>
    /**
     * WebSocket constructor to use.
     * If not specified, the global WebSocket constructor will be used or if not available will try to load it from the `ws` package.
     */
    websocket?: typeof WebSocket

    retry?: Retry
}

export function websocketConnectionFactory<T extends IOGateway.Message<unknown>>(opts?: WebSocketConnectionFactoryOptions<T>): WebsocketConnectionFactory<T> {
    const codec = opts?.codec ?? GatewayEncoders.json();
    const retry = opts?.retry;
    const websocket = opts?.websocket;
    return new WsConnectionFactory<T>(codec, retry, websocket);
}
